import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "./supabase";
import { adminApi, AdminApiError } from "./adminApi";

export type GateState = "loading" | "unauthenticated" | "denied" | "admin";

export interface AdminIdentity {
  userId: string;
  email: string;
  grantedAt: string;
}

interface GateValue {
  state: GateState;
  identity: AdminIdentity | null;
}

const PlatformAdminContext = createContext<GateValue | null>(null);

// Single owner of the platform-admin gate. Runs exactly one effect and holds
// exactly one auth subscription for the whole app. App.tsx and Layout.tsx read
// it via usePlatformAdmin() below.
export function PlatformAdminProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>("loading");
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);

  // Mirror of `state` readable from the auth-change callback, whose closure
  // would otherwise see the stale initial value.
  const stateRef = useRef<GateState>(state);
  stateRef.current = state;

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;

      if (!sessionData.session) {
        setState("unauthenticated");
        setIdentity(null);
        return;
      }

      try {
        const me = await adminApi.whoami();
        if (cancelled) return;
        setIdentity({
          userId: me.userId,
          email: me.email,
          grantedAt: me.grantedAt,
        });
        setState("admin");
      } catch (err) {
        if (cancelled) return;
        // Distinguish "no session" (expired/absent JWT → send to /login) from
        // "authenticated but not admin" (→ /access-denied). adminApi throws a
        // 401 with body.error === "no_session" when there is no access token.
        setIdentity(null);
        if (
          err instanceof AdminApiError &&
          err.status === 401 &&
          (err.body as { error?: string } | undefined)?.error === "no_session"
        ) {
          setState("unauthenticated");
          return;
        }
        setState("denied");
        console.error("[usePlatformAdmin] whoami failed", err);
      }
    };

    check();

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      if (event === "SIGNED_OUT") {
        // No point calling whoami — there's no session.
        setState("unauthenticated");
        setIdentity(null);
        return;
      }
      if (event === "TOKEN_REFRESHED" && stateRef.current === "admin") {
        // A silent refresh while already admin doesn't change authorization;
        // avoid a redundant whoami round-trip.
        return;
      }
      check();
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return createElement(
    PlatformAdminContext.Provider,
    { value: { state, identity } },
    children,
  );
}

export function usePlatformAdmin(): GateValue {
  const ctx = useContext(PlatformAdminContext);
  if (!ctx) {
    throw new Error(
      "usePlatformAdmin must be used within a PlatformAdminProvider",
    );
  }
  return ctx;
}
